import i18n from '@opsbase/lib-i18n';
import Pages from '@opsbase/lib-pages';
import Validator from '@opsbase/lib-validator';
import useOrgTemplateCopy from '@opsbase/react-gql-hooks/orgtemplate/useOrgTemplateCopy';
import useOrgTemplateCopyFromOpDefinition from '@opsbase/react-gql-hooks/orgtemplate/useOrgTemplateCopyFromOpDefinition';
import useOrgTemplateCreate from '@opsbase/react-gql-hooks/orgtemplate/useOrgTemplateCreate';
import OpDefinitionChooser from '@opsbase/web-data-choosers/OpDefinitionChooser';
import OrgTemplateChooser from '@opsbase/web-data-choosers/OrgTemplateChooser';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import WizardDialog from '@opsbase/web-dialogs/WizardDialog';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import OpTypePicker from '@opsbase/web-pickers/OpTypePicker/OpTypePicker';
import TextField from '@opsbase/web-textfield/TextField';
import RadioGroup from '@opsbase/web-ui/RadioGroup';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useMemo, useState } from 'react';
// eslint-disable-next-line no-shadow
var CreateType;
(function (CreateType) {
    CreateType["EMPTY"] = "empty";
    CreateType["CLONE"] = "clone";
    CreateType["CLONE_OPPLAN"] = "cloneFromOpPlan";
})(CreateType || (CreateType = {}));
export function OrgTemplateCreateDialog() {
    const [state, setState] = useState({
        type: CreateType.EMPTY,
        selectedOrgTemplates: [],
        selectedOpDefinitions: [],
        selectedOrgs: [],
        title: '',
        opType: undefined,
    });
    const { raise } = useNotificationActions();
    const { replaceTo } = useNav();
    const createOrgTemplate = useOrgTemplateCreate();
    const copyOrgTemplate = useOrgTemplateCopy();
    const copyOrgTemplatesFromOpPlan = useOrgTemplateCopyFromOpDefinition();
    const handleCreate = useCallback(async () => {
        const { selectedOrgTemplates, selectedOpDefinitions } = state;
        let createPromise;
        if (state.type === CreateType.EMPTY) {
            Validator.validate(state);
            createPromise = createOrgTemplate({
                patch: {
                    title: state.title,
                    type: state.opType,
                },
            });
        }
        else if (state.type === CreateType.CLONE) {
            createPromise = copyOrgTemplate({
                ids: selectedOrgTemplates.map(d => d.id),
            });
        }
        else {
            createPromise = copyOrgTemplatesFromOpPlan({
                ids: selectedOpDefinitions.map(d => d.id),
            });
        }
        return raise(createPromise.then(template => {
            if (template.length)
                template = template[0];
            replaceTo(Pages.ORG_TEMPLATE_EDIT, { id: template.id });
        }), i18n.t('create-template-successful'));
    }, [copyOrgTemplate, copyOrgTemplatesFromOpPlan, createOrgTemplate, raise, replaceTo, state]);
    const handleChangeOpType = useCallback(opType => {
        setState({ ...state, opType });
    }, [state]);
    const handleChangeType = useCallback(type => {
        setState({ ...state, type });
    }, [state]);
    const handleChangeTitle = useCallback(title => {
        setState({ ...state, title });
    }, [state]);
    const handleSelectTemplate = useCallback((selectedOrgTemplates) => {
        setState({ ...state, selectedOrgTemplates });
    }, [state]);
    const handleSelectOpDefinition = useCallback((selectedOpDefinitions) => {
        setState({ ...state, selectedOpDefinitions });
    }, [state]);
    const steps = useMemo(() => {
        const _steps = [
            React.createElement(RadioGroup, { value: state.type, onChange: handleChangeType, options: [
                    {
                        label: i18n.t('create-empty-template'),
                        value: CreateType.EMPTY,
                    },
                    {
                        label: i18n.t('copy-existing-template'),
                        value: CreateType.CLONE,
                    },
                    {
                        label: i18n.t('create-template-from-opplan'),
                        value: CreateType.CLONE_OPPLAN,
                    },
                ] }),
        ];
        if (state.type === CreateType.EMPTY) {
            _steps.push(React.createElement(Vertical, { gutter: true },
                React.createElement(Pane, null,
                    React.createElement(Text, null, i18n.t('name-of-template-label')),
                    React.createElement(TextField, { white: true, fullWidth: true, value: state.title, autoFocus: true, validator: Validator.schemas().title, onChange: handleChangeTitle })),
                React.createElement(Pane, null,
                    React.createElement(Text, null, i18n.t('name-of-optype-label')),
                    React.createElement(OpTypePicker, { fullWidth: true, noMargin: true, white: true, value: state.opType, onChange: handleChangeOpType }))));
        }
        if (state.type === CreateType.CLONE) {
            _steps.push(React.createElement(React.Fragment, null,
                React.createElement(Text, null, i18n.t('select-templates')),
                React.createElement(OrgTemplateChooser, { multi: true, value: state.selectedOrgTemplates, onChange: handleSelectTemplate })));
        }
        if (state.type === CreateType.CLONE_OPPLAN) {
            _steps.push(React.createElement(Vertical, { gutter: true },
                React.createElement(Pane, null,
                    React.createElement(Text, null, i18n.t('select-op-plan')),
                    React.createElement(OpDefinitionChooser, { multi: true, value: state.selectedOpDefinitions, onChange: handleSelectOpDefinition }))));
        }
        return _steps;
    }, [
        handleChangeTitle,
        handleChangeType,
        handleChangeOpType,
        handleSelectOpDefinition,
        handleSelectTemplate,
        state,
    ]);
    return useMemo(() => (React.createElement(WizardDialog, { size: DialogSize.X800_Y400, title: i18n.t('create-new-template'), steps: steps, onComplete: handleCreate })), [handleCreate, steps]);
}
export default OrgTemplateCreateDialog;
